// src/store/omieSync.js
// Polling Omie — roda a cada 5min dentro do horário comercial
// Detecta faturamentos, aplica SAIDA_OMIE, registra auditoria, notifica operador

import {
  buscarNFsFaturadas,
  extrairItensNF,
  getLastCheck,
  setLastCheck,
  dentroDoHorarioComercial,
  POLL_INTERVAL_MS,
  OMIE_CREDS,
} from '../lib/omie.js'
import { setGlobalProducts, saveMovimento } from './products.js'
import { logEvent } from './eventLog.js'
import { todayStr } from '../data/seed.js'
import { supabase } from '../lib/supabase.js'

const LS_NFS_PROCESSADAS="estoqueos_nfs_processadas";

// ─── Timestamp de corte — só processa NFs após ativação do sync ───────────

async function getSyncActivatedAt(){
  try{
    const{data}=await supabase.from('config').select('value').eq('key','omie_sync_activated_at').single();
    return data?.value||null;
  }catch(e){return null;}
}

async function setSyncActivatedAt(ts){
  try{
    await supabase.from('config').upsert({key:'omie_sync_activated_at',value:inicioDia.toISOString()},{onConflict:'key'});  }catch(e){console.warn('setSyncActivatedAt error',e);}
}

// ─── NFs já processadas (evita duplicar saídas) ────────────────────────────
const _nfsProcessadas=new Set();
let _nfsCarregadas=false;

async function getNfsProcessadas(){
  if(_nfsCarregadas)return _nfsProcessadas;
  try{
    const{data}=await supabase.from('config').select('value').eq('key','nfs_processadas').single();
    if(data?.value){JSON.parse(data.value).forEach(c=>_nfsProcessadas.add(c));}
  }catch(e){}
  _nfsCarregadas=true;
  return _nfsProcessadas;
}
async function addNfProcessada(chave){
  _nfsProcessadas.add(chave);
  const arr=[..._nfsProcessadas].slice(-500);
  try{
    await supabase.from('config').upsert({key:'nfs_processadas',value:JSON.stringify(arr)},{onConflict:'key'});
  }catch(e){}
}

// ─── Listeners de notificação (botão AVISOS) ───────────────────────────────
let _avisos=[];
let _avisoListeners=[];

// ─── Sync status ───────────────────────────────────────────────────────────
let _syncStatus='idle'; // 'idle' | 'syncing' | 'error'
let _lastSyncTs=null;
let _syncListeners=[];
function subSyncStatus(fn){
  _syncListeners.push(fn);
  return()=>{_syncListeners=_syncListeners.filter(f=>f!==fn);};
}
function getSyncStatus(){return{status:_syncStatus,lastTs:_lastSyncTs};}
function _setSyncStatus(s){
  _syncStatus=s;
  _syncListeners.forEach(f=>f({status:s,lastTs:_lastSyncTs}));
}

function subAvisos(fn){
  _avisoListeners.push(fn);
  return()=>{_avisoListeners=_avisoListeners.filter(f=>f!==fn);};
}
function getAvisos(){return _avisos;}
function marcarAvisoLido(id){
  _avisos=_avisos.map(a=>a.id===id?{...a,lido:true}:a);
  _avisoListeners.forEach(f=>f(_avisos));
}
function limparAvisos(){
  _avisos=[];
  _avisoListeners.forEach(f=>f(_avisos));
  supabase.from('notificacoes').delete().eq('para','operator')
    .then(({error})=>{if(error)console.warn('[avisos] limpar supabase error',error);});
}

// ─── Lógica principal de sync ──────────────────────────────────────────────
let _products=[];

// omieSync precisa de acesso à lista atual de produtos
// App.jsx chama setProductsRef sempre que products mudar
function setProductsRef(list){_products=list;}
function pushAviso(aviso){
  _avisos=[aviso,..._avisos].slice(0,50);
  _avisoListeners.forEach(f=>f(_avisos));
  supabase.from('notificacoes').insert({
    id:String(aviso.id),
    tipo:aviso.tipo,
    titulo:aviso.titulo,
    mensagem:aviso.mensagem,
    lida:aviso.lido??false,
    para:'operator',
    created_at:aviso.ts,
  }).then(({error})=>{if(error)console.warn('[avisos] persist error',error);});
}

const MAX_LOOKBACK_MS=3*24*60*60*1000; // 3 dias

async function sincronizarEmpresa(empresa,corteTs){
  const raw=await getLastCheck();
  const teto=new Date(Date.now()-MAX_LOOKBACK_MS).toISOString();
  const desde=(!raw||new Date(raw)<new Date(teto))?teto:raw;
  const nfs=await buscarNFsFaturadas(empresa,desde);
  console.log(`[omieSync] ${empresa}: ${nfs?.length??0} NF(s) desde ${desde.slice(0,10)}`);
  if(!nfs||nfs.length===0)return;

  const processadas=await getNfsProcessadas();

  for(const nf of nfs){
    const chaveNF=String(nf.compl?.cChaveNFe??nf.chNFe??nf.chave_nfe??nf.ide?.nNF??"");
    if(!chaveNF||processadas.has(chaveNF))continue;

    // Ignora NFs anteriores ao timestamp de ativação do sync
    if(corteTs){
      const dataNF=nf.ide?.dEmi||nf.dhEmi||nf.data_emissao||"";
      let tsNF=0;
      if(dataNF){
        if(dataNF.includes('/')){
          const[_d,_m,_y]=dataNF.split('/');
          tsNF=new Date(`${_y}-${_m}-${_d}T00:00:00Z`).getTime();
        } else {
          tsNF=new Date(dataNF.length===10?dataNF+'T00:00:00Z':dataNF).getTime();
        }
      }
      const tsCorte=new Date(corteTs).getTime();
      if(tsNF&&tsNF<tsCorte)continue;
    }

    const itens=extrairItensNF(nf,empresa);

    for(const item of itens){
      // tenta encontrar produto pelo sku_fornecedor ou nome
      const skuForn=item.sku_forn?.trim().toUpperCase();
      const descNorm=item.descricao?.toLowerCase().trim();
      const prod=_products.find(p=>{
        // 1. SKU fornecedor exato
        if(skuForn&&p.sku_fornecedor?.trim().toUpperCase()===skuForn)return true;
        // 2. Nome: produto contém os primeiros 10 chars da descrição Omie
        if(descNorm?.length>=6){
          const slice=descNorm.slice(0,10);
          if(p.name.toLowerCase().includes(slice))return true;
        }
        // 3. Descrição Omie contém o nome do produto (match invertido)
        if(descNorm&&p.name.length>=6&&descNorm.includes(p.name.toLowerCase().slice(0,10)))return true;
        return false;
      });

      if(!prod){
        // produto não encontrado — registra na auditoria para revisão manual
        logEvent({
          tipo_evento:"SAIDA_OMIE_NAO_IDENTIFICADA",
          usuario:"Omie",
          entidade:"NF",
          id_entidade:item.numero_nf||"—",
          detalhes:`Item não identificado: "${item.descricao}" | ${item.qty}un | ${empresa} | NF ${item.numero_nf}`,
        });
        pushAviso({
          id:crypto.randomUUID(),
          tipo:"nao_identificado",
          cor:"#ffa502",
          titulo:"Item não identificado no faturamento",
          mensagem:`"${item.descricao}" (${item.qty}un) — NF ${item.numero_nf} | ${empresa}`,
          ts:new Date().toISOString(),
          lido:false,
        });
        continue;
      }

      // aplica saída automática
      const deposito=prod.croma>=item.qty?"croma":"esfera";
      const mv={
        id:crypto.randomUUID(),
        type:"saida",
        location:deposito==="croma"?"CROMA":"ESFERA LOCAÇÃO",
        qty:item.qty,
        note:`Faturamento Omie — NF ${item.numero_nf} | ${empresa}`,
        operator:"Omie",
        date:todayStr(),
      };

      saveMovimento(prod.id,mv,p=>{
        const key=p.croma>=item.qty?"croma":"esfera";
        const next={...p};
        next[key]=Math.max(0,(p[key]||0)-item.qty);
        next.movements=[mv,...p.movements];
        return next;
      });

      logEvent({
        tipo_evento:"SAIDA_OMIE",
        usuario:"Omie",
        entidade:"PRODUTO",
        id_entidade:prod.sku,
        detalhes:`−${item.qty}un | NF ${item.numero_nf} | ${empresa} | "${item.descricao}"`,
      });

      pushAviso({
        id:crypto.randomUUID(),
        tipo:"saida_omie",
        cor:"#00d084",
        titulo:`Saída automática — ${prod.sku}`,
        mensagem:`−${item.qty}un via NF ${item.numero_nf} | ${empresa}`,
        ts:new Date().toISOString(),
        lido:false,
      });
    }

    addNfProcessada(chaveNF);
  }
}

async function executarSync(){
  if(!dentroDoHorarioComercial())return;
  _setSyncStatus('syncing');
  try{
    let corteTs=await getSyncActivatedAt();
    if(!corteTs){
      corteTs=new Date().toISOString();
      await setSyncActivatedAt(corteTs);
      console.log('[omieSync] Timestamp de corte gravado:',corteTs);
    }
    const empresas=Object.keys(OMIE_CREDS);
    for(const e of empresas){
      await sincronizarEmpresa(e,corteTs);
      await new Promise(r=>setTimeout(r,2000));
    }
    await setLastCheck(new Date().toISOString());
    _lastSyncTs=new Date().toISOString();
    _setSyncStatus('idle');
  }catch(e){
    console.warn('[omieSync] executarSync error',e);
    _setSyncStatus('error');
  }
}

// ─── Inicialização do polling ──────────────────────────────────────────────
let _pollTimer=null;

// SKU antigo: CR + dígitos sem traço (ex: CR0013). Novo formato tem traço (EQP-19052025-00001).
const _OLD_SKU_RE=/\bCR\d{3,}\b/;

function iniciarPolling(){
  if(_pollTimer)return;
const _janela=new Date(Date.now()-MAX_LOOKBACK_MS).toISOString();
  supabase.from('notificacoes')
    .select('*').eq('para','operator').eq('lida',false)
    .gte('created_at',_janela)
    .order('created_at',{ascending:false}).limit(50)
    .then(({data})=>{
      if(!data||data.length===0)return;
      const obsoletos=data.filter(r=>_OLD_SKU_RE.test(r.titulo||''));
      const validos=data.filter(r=>!_OLD_SKU_RE.test(r.titulo||''));
      if(obsoletos.length>0){
        const ids=obsoletos.map(r=>r.id);
        supabase.from('notificacoes').delete().in('id',ids)
          .then(()=>console.log(`[avisos] ${obsoletos.length} aviso(s) obsoleto(s) removido(s)`));
      }
      if(validos.length>0){
        _avisos=validos.map(r=>({
          id:r.id,tipo:r.tipo,titulo:r.titulo,mensagem:r.mensagem,
          cor:r.tipo==='nao_identificado'?'#ffa502':'#00d084',
          ts:r.created_at,lido:r.lida,
        }));
        _avisoListeners.forEach(f=>f(_avisos));
      }
    });
  // Limpa do Supabase tudo que é mais antigo que 3 dias (sem trazer para memória)
  supabase.from('notificacoes').delete().eq('para','operator').lt('created_at',_janela)
    .then(({error})=>{if(error)console.warn('[avisos] purge antigos error',error);});
  executarSync();
  _pollTimer=setInterval(executarSync,POLL_INTERVAL_MS);
}

function pararPolling(){
  if(_pollTimer){clearInterval(_pollTimer);_pollTimer=null;}
}

export {
  iniciarPolling,
  pararPolling,
  setProductsRef,
  subAvisos,
  pushAviso,
  getAvisos,
  marcarAvisoLido,
  limparAvisos,
  executarSync,
  subSyncStatus,
  getSyncStatus,
};